import { Navigate, createBrowserRouter } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

/** The one 3D review page (founder 2026-08-02): old per-lane viewer URLs
 * forward into /3d with their deep-link params preserved. */
function RedirectTo3D({ lane }: { lane: string }) {
  const params = new URLSearchParams(window.location.search);
  params.set('lane', lane);
  return <Navigate to={`/3d?${params.toString()}`} replace />;
}
import RootLayout from './layouts/RootLayout';
import AuthLayout from './layouts/AuthLayout';
import PublicLayout from './layouts/PublicLayout';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import FaqPage from './pages/FaqPage';
import MultiplayerGuidePage from './pages/MultiplayerGuidePage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import BuyTokensPage from './pages/BuyTokensPage';
import GetTheAppPage from './pages/GetTheAppPage';
import DeckPage from './pages/DeckPage';
import PartnershipPage from './pages/PartnershipPage';
import CreatorAboutPage from './pages/CreatorAboutPage';
import NotFoundPage from './pages/NotFoundPage';
import DiscordOAuthCallbackPage from './pages/DiscordOAuthCallbackPage';
import UnsubscribePage from './pages/UnsubscribePage';
import AuthActionPage from './pages/AuthActionPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LobbyLayout from './components/layout/LobbyLayout';
import GameLayout from './components/layout/GameLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import AdminPage from './pages/AdminPage';

const routes: RouteObject[] = [
  // Public routes — sidebar + video background, visible to everyone
  {
    element: <PublicLayout />,
    errorElement: (
      <ErrorBoundary>
        <NotFoundPage />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/',
        element: <LandingPage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'faq',
        element: <FaqPage />,
      },
      {
        path: 'multiplayer',
        element: <MultiplayerGuidePage />,
      },
      {
        path: 'tos',
        element: <TermsOfService />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'delete-account',
        element: <DeleteAccount />,
      },
      // Public unsubscribe page — no auth needed, uid+sig in query string
      {
        path: 'unsubscribe',
        element: <UnsubscribePage />,
      },
      // Public auth-action handler — verify/reset/change links we send to our
      // own domain (mode + oobCode in query string). No auth needed.
      {
        path: 'auth/action',
        element: <AuthActionPage />,
      },
      // Auth routes — redirect to dashboard if already logged in
      {
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'signup',
            element: <SignupPage />,
          },
          {
            path: 'reset-password',
            element: <ResetPasswordPage />,
          },
        ],
      },
      {
        path: 'verify-email',
        element: <VerifyEmailPage />,
      },
      {
        path: 'get-the-app',
        element: <GetTheAppPage />,
      },
      {
        path: 'deck',
        element: <DeckPage />,
      },
      {
        path: 'partnership',
        element: <PartnershipPage />,
      },
      {
        path: 'creator/adventures',
        element: <CreatorAboutPage />,
      },
    ],
  },
  // Protected routes — require authentication
  {
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    errorElement: (
      <ErrorBoundary>
        <NotFoundPage />
      </ErrorBoundary>
    ),
    children: [
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'lobby',
        element: <LobbyLayout />,
      },
      {
        path: 'buy-tokens',
        element: <BuyTokensPage />,
      },
      {
        path: 'play/:sessionId',
        element: <GameLayout />,
      },
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        ),
      },
    ],
  },
  // Odyssey R&D experiment — DEV-ONLY, standalone, no auth. Gated behind
  // `import.meta.env.DEV` (statically false in a production build) and loaded
  // via a dynamic import inside the dead branch, so Rollup drops both the page
  // chunk and its draft scene art from the production bundle. Its media under
  // `public/odyssey-test/` is stripped from dist by the `emit-version-txt`
  // plugin's closeBundle above.
  ...(import.meta.env.DEV
    ? [
        {
          path: 'odyssey-test',
          lazy: async () => ({
            Component: (await import('./pages/odyssey-test/OdysseyTestPage')).default,
          }),
        } satisfies RouteObject,
      ]
    : []),
  // 3D combat world dev harness (P3 renderer slice). DEV-ONLY: same
  // dead-branch dynamic-import pattern as odyssey-test above, so the page
  // and the three.js stack are dropped from production bundles.
  // VITE_DEV_VIEWERS=1 (the local release preview, scripts/
  // preview-release-web.sh) re-enables the review viewers in a production
  // build; real deploys leave it unset so the branch stays dead.
  ...(import.meta.env.DEV || import.meta.env.VITE_DEV_VIEWERS === '1'
    ? [
        {
          // THE 3D review page: scenes, heroes, creatures, items, bench.
          path: '3d',
          lazy: async () => ({
            Component: (await import('./pages/viewer3d/Viewer3DPage')).default,
          }),
        } satisfies RouteObject,
        { path: 'world-dev', element: <RedirectTo3D lane="bench" /> } satisfies RouteObject,
        { path: 'char-viewer', element: <RedirectTo3D lane="chars" /> } satisfies RouteObject,
        { path: 'round-viewer', element: <RedirectTo3D lane="rounds" /> } satisfies RouteObject,
        { path: 'hero-viewer', element: <RedirectTo3D lane="heroes" /> } satisfies RouteObject,
        { path: 'scene-viewer', element: <RedirectTo3D lane="scenes" /> } satisfies RouteObject,
        {
          // Sprite bake bench: rigged hero clips -> 8-direction albedo +
          // normal sprite sheets (the 2.5D prerender lane).
          path: 'sprite-bake',
          lazy: async () => ({
            Component: (await import('./pages/sprite-bake/SpriteBakePage')).default,
          }),
        } satisfies RouteObject,
        { path: 'item-viewer', element: <RedirectTo3D lane="items" /> } satisfies RouteObject,
      ]
    : []),
  // Discord OAuth popup redirect target — bare page, relays the code to the
  // opener and closes. No layout, no auth.
  {
    path: 'discord-oauth-callback',
    element: <DiscordOAuthCallbackPage />,
  },
  {
    // Catch-all 404 route
    path: '*',
    element: <NotFoundPage />,
  },
];

export const router = createBrowserRouter(routes);
